import posthog from 'posthog-js';
import { AnalyticsEvents, type AnalyticsEvent } from '@bookkeeper/shared/analytics-events';
import { getUtmParams } from '@/lib/utm';
import {
  inferSignupEntityType,
  signupEntityTypeFromSearch,
  type SignupEntityType,
} from '@/lib/auth-url';

type LandingAnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

/**
 * Funnel attribution for landing events. Wider than SignupEntityType on
 * purpose: 'implementations' is an analytics dimension for the /wdrozenia
 * door, not a signup-URL entity; inferSignupEntityType stays 'jdg'|'biuro'
 * because app.bookeper.pl's signup contract only knows those two.
 */
function explicitLandingEntityType(
  pathname: string
): 'implementations' | SignupEntityType | undefined {
  if (pathname === '/wdrozenia' || pathname.endsWith('/wdrozenia')) return 'implementations';
  if (pathname === '/biura' || pathname.endsWith('/biura')) return 'biuro';
  if (pathname === '/jdg' || pathname.endsWith('/jdg')) return 'jdg';
  return undefined;
}

function inferLandingEntityType(
  pathname: string,
  search = ''
): 'implementations' | SignupEntityType {
  return (
    explicitLandingEntityType(pathname) ??
    signupEntityTypeFromSearch(search) ??
    inferSignupEntityType(pathname)
  );
}

function inferDoorEntityType(
  door: 'jdg' | 'biura' | 'wdrozenia'
): 'implementations' | SignupEntityType {
  if (door === 'wdrozenia') return 'implementations';
  if (door === 'biura') return 'biuro';
  return 'jdg';
}

function buildLandingProperties(properties: LandingAnalyticsProperties = {}) {
  if (typeof window === 'undefined') {
    return {
      source_surface: 'landing',
      entity_type: properties.entity_type ?? 'jdg',
      ...getUtmParams(''),
      ...properties,
    };
  }

  return {
    source_surface: 'landing',
    page_path: window.location.pathname,
    page_url: window.location.href,
    entity_type:
      properties.entity_type ??
      inferLandingEntityType(window.location.pathname, window.location.search),
    ...getUtmParams(window.location.search),
    ...properties,
  };
}

function trackLandingEvent(event: AnalyticsEvent, properties: LandingAnalyticsProperties = {}) {
  posthog.capture(event, buildLandingProperties(properties));
}

export function trackLandingCtaClicked(
  location: string,
  ctaLabel: string,
  properties: LandingAnalyticsProperties = {}
) {
  trackLandingEvent(AnalyticsEvents.LANDING_CTA_CLICKED, {
    location,
    cta_label: ctaLabel,
    ...properties,
  });
}

export function trackLandingSectionViewed(
  section: string,
  properties: LandingAnalyticsProperties = {}
) {
  trackLandingEvent(AnalyticsEvents.LANDING_SECTION_VIEWED, {
    section,
    location: section,
    ...properties,
  });
}

export function trackLandingDoorClicked(
  door: 'jdg' | 'biura' | 'wdrozenia',
  properties: LandingAnalyticsProperties = {}
) {
  trackLandingEvent(AnalyticsEvents.LANDING_DOOR_CLICKED, {
    door,
    location: 'door_band',
    ...properties,
    entity_type: properties.entity_type ?? inferDoorEntityType(door),
  });
}
